import { useMemo } from "react";
import type { OfferTemplate } from "@b2bportal/media-ads-api";
import type { Tile } from "@b2bportal/rbc-deal-tiles-api";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import { useFetchHotelFundedOffersQuery } from "@/modules/hotel-funded-offers/store";
import {
  type AdWithOfferTemplate,
  type HtsMediaDealTilePlacement,
  useHtsMediaDealTiles,
} from "@/modules/htsMedia/hooks/useHtsMediaDealTiles";

export type TravelDeal =
  | { kind: "claimed"; key: string; offer: OfferTemplate }
  | { kind: "sponsored"; key: string; ad: AdWithOfferTemplate }
  | { kind: "funded"; key: string; tile: Tile };

/** Loaded sponsored offers, then claimable sponsored tiles, then funded hotel deals (Figma 2002:40181 / 2002:40532). */
export const useTravelDeals = (
  placement: HtsMediaDealTilePlacement,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { isExperimentVariant } = useExperiment();
  const fundedEnabled =
    enabled &&
    isExperimentVariant(
      Experiments.HOTEL_FUNDED_OFFERS.name,
      Experiments.HOTEL_FUNDED_OFFERS.variants.available
    );

  const { data, isLoading: fundedLoading } = useFetchHotelFundedOffersQuery(
    undefined,
    { skip: !fundedEnabled }
  );
  const {
    ads,
    claimedOffers,
    isLoading: adsLoading,
  } = useHtsMediaDealTiles(placement, { enabled });

  const deals = useMemo<TravelDeal[]>(
    () => [
      ...claimedOffers.map((offer) => ({
        kind: "claimed" as const,
        key: `claimed-${offer.offerTemplateId}`,
        offer,
      })),
      ...ads.map((ad) => ({ kind: "sponsored" as const, key: ad.adId, ad })),
      ...(data?.tiles ?? [])
        .filter((tile) => tile.market != null)
        .map((tile) => ({
          kind: "funded" as const,
          key: tile.market.marketId,
          tile,
        })),
    ],
    [claimedOffers, ads, data]
  );

  return { deals, isLoading: fundedLoading || adsLoading };
};
